import { useState } from 'react'
import { Search, CheckCircle, XCircle, AlertTriangle, ExternalLink, FileText, Copy } from 'lucide-react'
import { verificationApi } from '../services/api'
import { SEOHead } from '../components/SEOHead'
import { VerificationResult } from '../types'

export function VerificationPage() {
  // State management for verification page
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<VerificationResult | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCopyContent = async () => {
    if (result?.content) {
      try {
        await navigator.clipboard.writeText(result.content)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy content:', err)
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!url.trim()) {
      setError('Please enter a website URL')
      return
    }

    try {
      setLoading(true)
      setError(null)
      setResult(null)

      const response = await verificationApi.verify(url.trim())

      if (response.success && response.data) {
        setResult(response.data)
      } else {
        setError(response.error || 'Failed to verify llm.txt file')
      }
    } catch (err) {
      setError('An error occurred while verifying the file')
    } finally {
      setLoading(false)
    }
  }

  const getStatusIcon = () => {
    if (!result) return null

    if (result.accessible && result.valid_format) {
      return <CheckCircle className="h-8 w-8 text-green-600" />
    } else if (result.accessible && !result.valid_format) {
      return <AlertTriangle className="h-8 w-8 text-yellow-600" />
    } else {
      return <XCircle className="h-8 w-8 text-red-600" />
    }
  }

  const getStatusMessage = () => {
    if (!result) return ''

    if (result.accessible && result.valid_format) {
      return 'Your llm.txt file is properly accessible and formatted!'
    } else if (result.accessible && !result.valid_format) {
      return 'Your llm.txt file is accessible but has formatting issues.'
    } else {
      return 'Your llm.txt file is not accessible or does not exist.'
    }
  }

  const getStatusColor = () => {
    if (!result) return ''

    if (result.accessible && result.valid_format) {
      return 'text-green-800'
    } else if (result.accessible && !result.valid_format) {
      return 'text-yellow-800'
    } else {
      return 'text-red-800'
    }
  }

  return (
    <>
      <SEOHead
        title="llm.txt Verification Tool - Check Your AI SEO File"
        description="Verify that your llm.txt file is properly accessible and formatted. Free tool to check if AI tools can read your website's llm.txt file correctly."
        keywords="llm.txt verification, AI SEO checker, llm.txt validator, website verification, AI optimization check"
        canonical="https://llmtext.net/verification"
      />
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="page-title">Verify Your llm.txt</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Check if your llm.txt file is properly accessible and formatted according to the official specification.
          </p>
        </div>

        {/* Verification Form */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="card">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="label">
                  Website URL
                </label>
                <div className="relative">
                  <input
                    type="url"
                    required
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="input-field pr-12"
                    placeholder="https://example.com"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
                <p className="help-text">
                  Enter your website's main URL. We'll check for llm.txt at the root directory.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Verify llm.txt
              </button>
            </form>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <XCircle className="h-5 w-5 text-red-600 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">
                    Verification Failed
                  </h3>
                  <p className="text-sm text-red-700 mt-1">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Verification Results */}
        {result && (
          <div className="max-w-4xl mx-auto">
            <div className="card">
              {/* Status Header */}
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  {getStatusIcon()}
                </div>
                <h2 className={`text-2xl font-semibold ${getStatusColor()}`}>
                  {getStatusMessage()}
                </h2>
                <p className="text-gray-600 mt-2">
                  Checked: <a href={result.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">
                    {result.url} <ExternalLink className="h-4 w-4 inline ml-1" />
                  </a>
                </p>
              </div>

              {/* Detailed Results */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Accessibility</h3>
                  <div className="flex items-center space-x-3">
                    {result.accessible ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className={result.accessible ? 'text-green-800' : 'text-red-800'}>
                      {result.accessible ? 'File is accessible' : 'File is not accessible'}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Format Validation</h3>
                  <div className="flex items-center space-x-3">
                    {result.valid_format ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className={result.valid_format ? 'text-green-800' : 'text-red-800'}>
                      {result.valid_format ? 'Format is valid' : 'Format has issues'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Issues */}
              {result.issues && result.issues.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <XCircle className="h-5 w-5 text-red-600 mr-2" />
                    Issues Found
                  </h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <ul className="space-y-2">
                      {result.issues.map((issue, index) => (
                        <li key={index} className="text-red-800 text-sm">
                          • {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Suggestions */}
              {result.suggestions && result.suggestions.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                    Suggestions for Improvement
                  </h3>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <ul className="space-y-2">
                      {result.suggestions.map((suggestion, index) => (
                        <li key={index} className="text-yellow-800 text-sm">
                          • {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* File Content */}
              {result.content && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <FileText className="h-5 w-5 text-gray-600 mr-2" />
                      File Content
                    </h3>
                    <button
                      onClick={handleCopyContent}
                      className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">
                      {result.content}
                    </pre>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    setResult(null)
                    setUrl('')
                  }}
                  className="btn-outline"
                >
                  Check Another URL
                </button>

                {!result.accessible && (
                  <a href="/generator" className="btn-primary">
                    Generate llm.txt File
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Help Section */}
        {!result && (
          <div className="max-w-4xl mx-auto mt-12">
            <div className="card">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                What We Check
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Accessibility</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• File exists at /llm.txt</li>
                    <li>• Returns HTTP 200 status</li>
                    <li>• Proper content type</li>
                    <li>• File is readable</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Format Validation</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Has H1 heading (website name)</li>
                    <li>• Contains blockquote summary</li>
                    <li>• Proper markdown structure</li>
                    <li>• Valid URL formats</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  )
}
